import React, { FC } from "react"
// Data, Lib
import { field_attrs } from "components/table/lib/column-field"
import { formatDate, parseDate } from "data/lib/dates"
import notificationService from "data/notification"
// Components
import { DateText_s } from "styles/input-styles"
import DropdownInput from "components/inputs/dropdown"
import NumberInput from "components/inputs/number"
import TextInput from "components/inputs/text"
// Styles
import { ValuationScenarioForm_s } from "components/property/valuation-settings/valuation-settings-styles"

interface ValuationScenario {
  valuationDescription: string
  valuationType: string
  valuationDateType: string
  valuationDate: string
  specifiedPrice: number
  goingInCapitalizationRate: number
  term: number
  discountRate: number
  discountRateIncrement: number
  terminalCap: number
  terminalCapIncrement: number
  costOfSales: number
  irrNpvDiscounting: string
  salesNoiCapYear: string
  abatementAdjustment: string
  vacancyLossAdjustment: string
  adjustSales: string
  saleAdjustmentAmount: number
  lastCalcValue: any
  lastCalcValuePerArea: any
}

interface ValuationScenarioProps {
  valuationScenario: ValuationScenario
  i: number
  persist: Function
  property: object
}

const ValuationScenarioForm: FC<ValuationScenarioProps> = (props) => {

  function validateTerm(i:number, val:number|string){
    const max = getUnderwritingTermMax();

    if (val === '') props.persist(i, 'term', val)
    else if (val < 3){
      props.persist(i, 'term', 3)
      notificationService.notify({
        type: "warn",
        message: "Valuation Term adjusted to the minimum term of 3",
        dismiss: 5000
      })
    } else if (val > max){
      notificationService.notify({
        type: "warn",
        message: "Valuation Term adjusted to valuation term max",
        dismiss: 5000
      })
      props.persist(i, 'term', max)
    } else {
      props.persist(i, 'term', val)
    }
  }

  function getUnderwritingTermMax():number {
    const { property, valuationScenario } = props;
    const analysisBegin = parseDate(property['analysisbegindate']);
    const reportBegin = parseDate(property['reportbegindate']);
    const valuationDate = parseDate(valuationScenario.valuationDate || reportBegin);
    const propertyTerm = property['term']

    let maxTerm = Math.floor(
      (12 * (analysisBegin.year() - valuationDate.year() + propertyTerm) +
        (analysisBegin.month() + 1 - (valuationDate.month() + 1))) /
      12
    )
    if (maxTerm > 20) maxTerm = 20;
    if (maxTerm < 1) maxTerm = 1;

    return maxTerm;
  }


  function getUnderwritingMaxDate():Date {
    const { property }      = props;
    const analysisBegin     = parseDate(property['analysisbegindate']);
    const propertyTerm      = property['term'];
    const analysisBeginDate = parseDate(analysisBegin);

    return analysisBeginDate.add(propertyTerm, 'years').subtract(1,'year').toDate()
  }

  function validateValuationDate(i:number, val:string):void {
    const { persist, property } = props;
    const analysisBegin         = parseDate(property['analysisbegindate']);
    const underwritingDateMax   = getUnderwritingMaxDate();

    if (parseDate(val).isBefore(parseDate(analysisBegin))){
      notificationService.notify({
        type: "warn",
        message: "Valuation Date adjusted to the analysis begin date",
        dismiss: 5000
      })
      persist(i, 'valuationDate', formatDate(analysisBegin));
    } else if (parseDate(val).isAfter(parseDate(underwritingDateMax))){
      notificationService.notify({
        type: "warn",
        message: "Valuation Date adjusted to the underwriting date max",
        dismiss: 5000
      })
      persist(i, 'valuationDate', formatDate(underwritingDateMax));
    } else {
      persist(i, 'valuationDate', formatDate(val));
    }
  }

  const attrs = (field) => field_attrs('property', 'valuationdetail')([props.i - 1, field])(props.property, props)

  let lastCalcValuePerArea:number|string = parseFloat(props.valuationScenario.lastCalcValuePerArea)
  const numFormat = Intl.NumberFormat('en-US');
  lastCalcValuePerArea = numFormat.format(lastCalcValuePerArea)
  let lastCalcValue:number|string = Math.round(props.valuationScenario.lastCalcValue)
  lastCalcValue = numFormat.format(lastCalcValue)

  return (
    <ValuationScenarioForm_s>
      <div>
        <label>Scenario #{props.i}</label>
      </div>
      <div>
        <TextInput
          value={props.valuationScenario.valuationDescription}
          persist={val => props.persist(props.i, 'valuationDescription', val)}
        />
      </div>
      <div>
        <DropdownInput
          field={'valuationType'}
          value={props.valuationScenario.valuationType}
          persist={(field, val) => props.persist(props.i, field, val)}
          attrs={attrs}
          options={['DCF', 'Direct Cap', 'Specified Price'].map(valuationType => { return { label: valuationType, value: valuationType }})}
        />
      </div>
      <div>
        <DropdownInput
          field={'valuationDateType'}
          value={props.valuationScenario.valuationDateType}
          persist={(field, val) => props.persist(props.i, field, val)}
          attrs={attrs}
          options={['Analysis Begin', 'Report Begin', 'Specified Date'].map(valuationDateType => { return { label: valuationDateType, value: valuationDateType }})}
        />
      </div>
      <div>
        <DateText_s
          
          disabled={props.valuationScenario.valuationDateType !== 'Specified Date'}
          disabledText="N/A"
          value={props.valuationScenario.valuationDate}
          persist={val => validateValuationDate(props.i, val)}
          withoutDays
          field="valuationDate"
          attrs={attrs}
        />
      </div>
      <div>
        <NumberInput
          readPrecision={0}
          writePrecision={0}
          value={props.valuationScenario.specifiedPrice}
          persist={(field, val) => props.persist(props.i, field, val)}
          field="specifiedPrice"
          disabled={props.valuationScenario.valuationType !== 'Specified Price'}
          disabledText="N/A"
          attrs={attrs}
        />
      </div>
      <div>
        <NumberInput
          readPrecision={4}
          writePrecision={4}
          value={props.valuationScenario.goingInCapitalizationRate}
          persist={(field, val) => props.persist(props.i, field, val)}
          field="goingInCapitalizationRate"
          disabled={props.valuationScenario.valuationType !== 'Direct Cap'}
          disabledText="N/A"
          attrs={attrs}
        />
      </div>
      <div>
        <NumberInput
          readPrecision={4}
          writePrecision={4}
          value={props.valuationScenario.term}
          persist={(field, val) => validateTerm(props.i, val)}
          field="term"
          attrs={attrs}
          disabled={props.valuationScenario.valuationType === 'Direct Cap'}
          disabledText="N/A"
        />
      </div>
      <div>
        <NumberInput
          readPrecision={4}
          writePrecision={4}
          value={props.valuationScenario.discountRate}
          persist={(field, val) => props.persist(props.i, field, val)}
          field="discountRate"
          disabled={props.valuationScenario.valuationType !== 'DCF'}
          disabledText="N/A"
          attrs={attrs}
        />
      </div>
      <div>
        <NumberInput
          readPrecision={4}
          writePrecision={4}
          value={props.valuationScenario.discountRateIncrement}
          persist={(field, val) => props.persist(props.i, field, val)}
          field="discountRateIncrement"
          disabled={props.valuationScenario.valuationType === 'Direct Cap'}
          disabledText="N/A"
          attrs={attrs}
        />
      </div>
      <div>
        <NumberInput
          readPrecision={4}
          writePrecision={4}
          value={props.valuationScenario.terminalCap}
          persist={(field, val) => props.persist(props.i, field, val)}
          field="terminalCap"
          disabled={props.valuationScenario.valuationType === 'Direct Cap'}
          disabledText="N/A"
          attrs={attrs}
        />
      </div>
      <div>
        <NumberInput
          readPrecision={4}
          writePrecision={4}
          value={props.valuationScenario.terminalCapIncrement}
          persist={(field, val) => props.persist(props.i, field, val)}
          field="terminalCapIncrement"
          attrs={attrs}
        />
      </div>
      <div>
        <NumberInput
          readPrecision={4}
          writePrecision={4}
          value={props.valuationScenario.costOfSales}
          persist={(field, val) => props.persist(props.i, field, val)}
          field="costOfSales"
          attrs={attrs}
        />
      </div>
      <div>
        <DropdownInput
          field={'irrNpvDiscounting'}
          value={props.valuationScenario.irrNpvDiscounting}
          persist={(field, val) => props.persist(props.i, field, val)}
          options={['Annual', 'Monthly'].map(option => { return { label: option, value: option }})}
          disabled={props.valuationScenario.valuationType === 'Direct Cap'}
          disabledText="N/A"
          attrs={attrs}
        />
      </div>
      <div>
        <DropdownInput
          field={'salesNoiCapYear'}
          value={props.valuationScenario.salesNoiCapYear}
          persist={(field, val) => props.persist(props.i, field, val)}
          // attrs={attrs}
          options={['Sale Year + 1', 'Sale Year'].map(option => { return { label: option, value: option }})}
          disabled={props.valuationScenario.valuationType === 'Direct Cap'}
          disabledText="N/A"
          attrs={attrs}
        />
      </div>
      { props.property['type'] !== 'Hospitality' && <div>
        <DropdownInput
          field={'abatementAdjustment'}
          value={props.valuationScenario.abatementAdjustment}
          persist={(field, val) => props.persist(props.i, field, val)}
          options={['No', 'Yes'].map(option => { return { label: option, value: option }})}
          attrs={attrs}
        />
      </div> }
      { props.property['type'] === 'Commercial' && <div>
        <DropdownInput
          field={'vacancyLossAdjustment'}
          value={props.valuationScenario.vacancyLossAdjustment}
          persist={(field, val) => props.persist(props.i, field, val)}
          options={['No', 'Yes'].map(option => { return { label: option, value: option }})}
          attrs={attrs}
        />
      </div>
      }
      <div>
        <DropdownInput
          field={'adjustSales'}
          value={props.valuationScenario.adjustSales}
          persist={(field, val) => props.persist(props.i, field, val)}
          options={['No', 'Yes'].map(option => { return { label: option, value: option }})}
          attrs={attrs}
        />
      </div>
      <div>
        <NumberInput
          readPrecision={2}
          writePrecision={2}
          value={props.valuationScenario.saleAdjustmentAmount}
          persist={(field, val) => props.persist(props.i, field, val)}
          field="saleAdjustmentAmount"
          attrs={attrs}
        />
      </div>
      <div>
        <span>{lastCalcValue ? `$${lastCalcValue}` : '-'}</span>
      </div>
      <div>
        <span>{lastCalcValuePerArea ? `$${lastCalcValuePerArea}` : '-'}</span>
      </div>
    </ValuationScenarioForm_s>
  )
}

export default ValuationScenarioForm
